import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAppStore from '../store/useAppStore';
import { ALL_BADGES } from '../data/badges';
import { BadgeCategory, BadgeDifficulty } from '../types';
import styles from './Achievements.module.css';

const categoryMeta: Record<BadgeCategory, { label: string; icon: string; color: string }> = {
  [BadgeCategory.LEARNING]: { label: '学习', icon: '📖', color: '#5a9e6f' },
  [BadgeCategory.PERSISTENCE]: { label: '坚持', icon: '🔥', color: '#e8734a' },
  [BadgeCategory.CHALLENGE]: { label: '挑战', icon: '⚔️', color: '#7b68ee' },
  [BadgeCategory.REVIEW]: { label: '复习', icon: '📝', color: '#e8a84a' },
  [BadgeCategory.EXPLORATION]: { label: '探索', icon: '🧭', color: '#4a9e8e' },
  [BadgeCategory.SPECIAL]: { label: '特殊', icon: '🎲', color: '#c96a9e' },
  [BadgeCategory.HIDDEN]: { label: '隐藏', icon: '❓', color: '#8b7b8b' },
};

const diffStars = (d: BadgeDifficulty) => d === BadgeDifficulty.HARD ? '⭐⭐⭐' : d === BadgeDifficulty.MEDIUM ? '⭐⭐' : '⭐';

const Achievements: React.FC = () => {
  const navigate = useNavigate();
  // 订阅底层数据字段而非 getBadgeSnapshot()，避免每次返回新对象导致无限重渲染
  const completedLevels = useAppStore((s) => s.completedLevels);
  const wrongQuestions = useAppStore((s) => s.wrongQuestions);
  const reviewCount = useAppStore((s) => s.reviewCount);
  const streak = useAppStore((s) => s.streak);
  const stageChallengeRecords = useAppStore((s) => s.stageChallengeRecords);
  const bonusStars = useAppStore((s) => s.bonusStars);
  const dailyFirstWinDate = useAppStore((s) => s.dailyFirstWinDate);
  const userRole = useAppStore((s) => s.userRole);
  const babyBorn = useAppStore((s) => s.babyBorn);
  const babyDate = useAppStore((s) => s.babyDate);
  const dueDate = useAppStore((s) => s.dueDate);
  const onboardingComplete = useAppStore((s) => s.onboardingComplete);
  const badgeAuxData = useAppStore((s) => s.badgeAuxData);

  const [activeCategory, setActiveCategory] = useState<BadgeCategory | 'ALL'>('ALL');

  const snapshot = useMemo(() => ({
    completedLevels: completedLevels.map((l) => ({ ...l })),
    wrongQuestions: wrongQuestions.map((w) => ({ ...w })),
    reviewCount,
    streak: { ...streak },
    stageChallengeRecords: { ...stageChallengeRecords },
    bonusStars,
    dailyFirstWinDate,
    userRole,
    babyBorn,
    babyDate,
    dueDate,
    onboardingComplete,
    ...badgeAuxData,
  }), [completedLevels, wrongQuestions, reviewCount, streak, stageChallengeRecords, bonusStars, dailyFirstWinDate, userRole, babyBorn, babyDate, dueDate, onboardingComplete, badgeAuxData]);

  const badgeStatus = useMemo(() => {
    return ALL_BADGES.map((b) => {
      const unlocked = b.unlocked(snapshot);
      return { ...b, unlocked };
    });
  }, [snapshot]);

  const unlockedCount = badgeStatus.filter((b) => b.unlocked).length;
  const totalCount = ALL_BADGES.length;

  const categoryCounts = useMemo(() => {
    const map: Record<string, { total: number; unlocked: number }> = {};
    badgeStatus.forEach((b) => {
      if (!map[b.category]) map[b.category] = { total: 0, unlocked: 0 };
      map[b.category].total++;
      if (b.unlocked) map[b.category].unlocked++;
    });
    return map;
  }, [badgeStatus]);

  const filteredBadges = activeCategory === 'ALL'
    ? badgeStatus
    : badgeStatus.filter((b) => b.category === activeCategory);

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          ← 返回
        </button>
        <h1 className={styles.title}>🏆 成就徽章墙</h1>
        <p className={styles.subtitle}>
          已解锁 <strong>{unlockedCount}</strong> / {totalCount} ({Math.round((unlockedCount / totalCount) * 100)}%)
        </p>
        {/* 进度条 */}
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ width: `${(unlockedCount / totalCount) * 100}%` }}
          />
        </div>
      </header>

      {/* 分类筛选 */}
      <div className={styles.filterBar}>
        <button
          className={`${styles.filterBtn} ${activeCategory === 'ALL' ? styles.filterActive : ''}`}
          onClick={() => setActiveCategory('ALL')}
        >
          全部
        </button>
        {Object.entries(categoryMeta).map(([cat, meta]) => {
          const counts = categoryCounts[cat as BadgeCategory] || { total: 0, unlocked: 0 };
          return (
            <button
              key={cat}
              className={`${styles.filterBtn} ${activeCategory === cat ? styles.filterActive : ''}`}
              onClick={() => setActiveCategory(cat as BadgeCategory)}
            >
              {meta.icon} {meta.label} {counts.unlocked}/{counts.total}
            </button>
          );
        })}
      </div>

      {/* 徽章网格 */}
      <div className={styles.grid}>
        {filteredBadges.map((badge) => {
          const meta = categoryMeta[badge.category];
          const showHidden = badge.isHidden && !badge.unlocked;

          return (
            <div
              key={badge.id}
              className={`${styles.badgeCard} ${badge.unlocked ? styles.badgeUnlocked : styles.badgeLocked}`}
              style={badge.unlocked ? { borderColor: meta.color } : undefined}
            >
              <div className={styles.badgeIconWrap} style={badge.unlocked ? { background: `${meta.color}15` } : undefined}>
                <span className={styles.badgeIcon}>
                  {showHidden ? '❓' : badge.icon}
                </span>
              </div>
              <div className={styles.badgeName}>
                {showHidden ? '???' : badge.name}
              </div>
              <div className={styles.badgeDesc}>
                {showHidden ? '达成条件后解锁' : badge.description}
              </div>
              <div className={styles.badgeMeta}>
                <span className={styles.badgeCategory} style={{ color: meta.color }}>
                  {meta.icon} {meta.label}
                </span>
                <span className={styles.badgeDiff}>{diffStars(badge.difficulty)}</span>
              </div>
              {badge.unlocked && (
                <div className={styles.unlockedOverlay}>✓</div>
              )}
            </div>
          );
        })}
      </div>

      {/* 空状态 */}
      {filteredBadges.length === 0 && (
        <div className={styles.empty}>
          <span className={styles.emptyIcon}>🔍</span>
          <p>该分类暂无徽章</p>
        </div>
      )}

      {/* 底部导航 */}
      <nav className={styles.bottomNav}>
        <span className={styles.navItem} onClick={() => navigate('/')}>
          首页
        </span>
        <span className={styles.navItem} onClick={() => navigate('/map')}>
          地图
        </span>
        <span className={styles.navItem} onClick={() => navigate('/review')}>
          复习
        </span>
        <span className={`${styles.navItem} ${styles.navActive}`}>成就</span>
      </nav>
    </div>
  );
};

export default Achievements;
